<template>
    <LoadingView v-if="isLoading" />
    <div v-else class="flex flex-col gap-8">

        <nav class="flex flex-none gap-4 justify-between items-center rounded-t-lg">
            <div class="flex-none">
                <FormButton
                    color="secondary"
                    icon="fa-chevron-left"
                    @click="prev ? $router.push(prev) : $router.go(-1)">
                    Back
                </FormButton>
            </div>
            <div class="py-4 sm:rounded-tr-lg flex justify-end items-center h-16 gap-2">
                <NotificationSubscription v-if="community?.id" :type="NotificationTypes.DIVISION" :id="community.id" :context="community" />
                <ToggleButton
                    v-if="GlobalPolicy.edit()"
                    id="publish-toggle"
                    on-color="green"
                    :checked="floorplan.status == 'active'"
                    @changed="changeStatus" label="Set Publishing Status" />
            </div>
        </nav>
        <header class="flex items-start gap-8">
            <div class="flex flex-col lg:flex-row gap-5 sm:gap-6 flex-1">
                <div class="flex flex-col gap-5 sm:gap-6 flex-1 justify-between">
                    <div class="flex flex-col lg:flex-row gap-5 justify-between flex-none">

                        <div class="flex-1 flex flex-col gap-2 lg:gap-0 order-2 lg:order-1">
                            <h1 class="text-3xl leading-9 font-headline font-medium antialiased  bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
                                {{ floorplan.meta.info?.name || floorplan.kova.MarketingName }}
                                    <span v-if="!['N/S',''].includes(floorplan?.kova.CustomFields.TextCommModelStatus1)" class="bg-oakwood-gray text-white px-2 rounded-full inline-block text-xs leading-5 relative bottom-1 ml-2">{{ floorplan?.meta?.info?.callout }}</span>
                            </h1>
                            <dl class="lg:flex gap-2">
                                <dt class="sr-only">
                                    Collection
                                </dt>
                                <dd class="mt-1 text-sm leading-5 text-bhav-blue-300 border-r border-r-bhav-blue-50 pr-2">
                                    <!-- <router-link :to="{name: 'collection-show', params: {id : floorplan.collection.id}}" class="text-bhav-blue-600"> -->
                                    {{ floorplan.collection.kova?.MarketingName }}
                                    <!-- </router-link> -->
                                </dd>
                                <dt class="mt-1 text-sm leading-5 text-bhav-blue-300">
                                    Kova Model ID:
                                </dt>
                                <dd class="mt-1 text-sm leading-5 text-bhav-blue-300 border-r border-r-bhav-blue-50 pr-2">
                                    {{ floorplan.kova?.ModelRID }}
                                </dd>
                                <dt class="sr-only">
                                    Name
                                </dt>
                                <dd class="mt-1 text-sm leading-5 text-bhav-blue-300">
                                    {{ floorplan.kova?.Name }}
                                </dd>

                            </dl>
                        </div>
                    </div>
                    <section class="flex flex-col w-full gap-2">
                        <h2 class="sr-only">Basic Information</h2>
                        <div class="flex flex-row w-full gap-2 pb-4">
                            <InfoText class="w-full" label="Sales Status" :icon="`fak ${!['Pre-Sale (VIP)', 'Off Market', 'Sold Out', 'Internal', '', 'N/S'].includes(kovaStatus) ? 'fa-bulb' : 'fa-bulb'}`" :value="kovaStatus" :color="!['Pre-Sale (VIP)', 'Off Market', 'Sold Out', 'Internal', '', 'N/S'].includes(kovaStatus) ? 'green' : 'red'" :bg-color="!['Pre-Sale (VIP)', 'Off Market', 'Sold Out', 'Internal', '', 'N/S'].includes(kovaStatus) ? 'green' : 'red'" />
                            <InfoText class="w-full" bg-color="gray" label="Type" icon="fak fa-home-single-fam" :value="floorplan.kova?.Type || '-'" />
                            <InfoText class="w-full" bg-color="gray" label="Price" icon="fak fa-dollar" :value="priceRange" />
                            <InfoText class="w-full" bg-color="gray" label="Square Footage" icon="fak fa-ruler" :value="sqftRange" />
                        </div>
                        <div class="flex flex-row w-full gap-2">
                            <InfoText class="w-full" bg-color="gray" label="Bedrooms" icon="fak fa-bed" :value="bedRange" />
                            <InfoText class="w-full" bg-color="gray" label="Full Bathrooms" icon="fak fa-bath" :value="fullBathRange" />
                            <InfoText class="w-full" bg-color="gray" label="Half Bathrooms" icon="fak fa-half-bath" :value="halfBathRange" />
                            <InfoText class="w-full" bg-color="gray" label="Garages" icon="fak fa-garage" :value="garageRange" />
                        </div>
                    </section>

                </div>
                <div class="w-full lg:w-1/5 flex">
                    <img v-if="floorplan.image" class="object-cover rounded-lg"
                        :src="floorplan.image" :alt="floorplan.kova?.Name"
                        :title="floorplan.kova?.Name">
                    <div v-else class="text-5xl bg-bhav-blue-300 rounded-lg text-white p-8 flex items-center justify-center w-full">
                        <span class="fak fa-floorplan"></span>
                    </div>
                </div>

            </div>
        </header>
        <TabPanes route="floorplan-tab" :tabs="tabs" v-slot="{tab}" via="via-white" to="to-white" class="gap-4 py-6">
            <InfoPane :floorplan="floorplan" @update="prepare" v-if="tab?.name === 'info'" />
            <FeaturesPane :item="floorplan" @update="prepare" v-if="tab?.name === 'features'" />
            <ImagePane :parent="floorplan" context="floorplan" @update="(image) => {floorplan.image = image.media?.url}" v-if="tab?.name === 'photos'" />  
            <VideoPane :item="floorplan" context="floorplan" @update="prepare" v-if="tab?.name === 'videos'" />
            <InteractivePane :item="floorplan" context="floorplan" @update="prepare" v-if="tab?.name === 'interactive'" />
            <PromotionsPane context="floorplans" :id="floorplan.id" v-if="tab?.name === 'promotions'" />
            <LogPane context="floorplan" v-if="tab?.name === 'logs'" />
                
            <template v-if="tab?.name === 'debugger'">
                <h2>Raw Data</h2>
                <pre>
                    {{ floorplan }}
                </pre>
            </template>
        </TabPanes>
    </div>


</template>

<script>
import axios from "axios";
import LoadingView from '@/components/LoadingView.vue';
import {FormButton, ToggleButton} from '@/components/form';
import {InfoPane} from '@/components/floorplans';
import {InteractivePane, ImagePane, VideoPane, LogPane, PromotionsPane} from '@/components/shared';
import InfoText from '@/components/media/InfoText.vue';
import { mapState } from "pinia";
import { useAuthStore } from "@/stores/auth";
import { handle404 } from "@/helpers/globals";
import TabPanes from "@/components/TabPanes.vue";
import Floorplan, { helpers } from "@/models/Floorplan";
import { GlobalPolicy } from "@/policies/global";
import Statuses from "@/enums/Statuses";
import FloorplanStatuses from "@/enums/FloorplanStatuses";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import NotificationTypes from "@/enums/NotificationTypes";
import Community from "@/models/community";
import FeaturesPane from "@/components/floorplans/FeaturesPane.vue";

let tabs = [
    {
        name: 'info',
        icon: 'fa-alert',
        title: 'General Information',
        acl: 0
    },
    {
        name: 'features',
        icon: 'fa-settings',
        title: 'Features',
        acl: 0
    },
    {
        name: 'photos',
        icon: 'fa-photo',
        title: 'Images',
        acl: 0
    },
    {
        name: 'videos',
        icon: 'fa-video',
        title: 'Videos',
        acl: 0
    },
    {
        name: 'interactive',
        icon: 'fa-3d',
        title: 'Interactives',
        acl: 0
    },
    {
        name: 'promotions',
        icon: 'fa-dollar',
        title: 'Promotions',
        acl: 2
    },
    {
        name: 'logs',
        icon: 'fa-history',
        title: 'History',
        acl: 1
    },
    {
        name: 'debugger',
        icon: 'fa-debug',
        title: 'Debugger',
        acl: 2
    },
];

export default {
    head() {
        return {
            title: () => `${this.$route.meta.title} %separator ${this.floorplan?.meta?.info?.name || this.floorplan?.kova?.MarketingName}`
        }
    },
    setup() {
        return {
            GlobalPolicy
        }
    },
    data() {
        return {
            floorplan: new Floorplan({id: this.$route.params.id}),
            revert: null,
            isLoading: true,
            tabs: [],
            prev: false,
            NotificationTypes,
            community: null
        }
    },
    methods: {
        show() {
            axios.get(`floorplans/${this.floorplan.id}`)
                .then(res => {
                    this.floorplan = new Floorplan(res.data);
                    this.isLoading = false;
                    this.showCommunity();
                })
                .catch(err => {
                    if(err.response.status === 404) 
                        handle404(this, err.response.data.message);
                });
        },
        showCommunity() {
            axios.get(`/collections/${this.floorplan.collection.id}`)
            .then(resp => {
                this.community = new Community(resp.data.community)
            })
        },
        changeStatus(toggle) {
            this.floorplan.status = toggle ? 'active' : 'inactive';
            this.save();
        },
        save() {
            this.isLoading = true;
            axios.patch(`floorplans/${this.floorplan.id}`, this.floorplan)
                .catch(e => {
                    this.floorplan = new Floorplan(this.revert);
                    throw e;
                })
                .finally(() => {
                    this.revert = null;
                    this.isLoading = false;
                });
        },
        prepare(value) {
            this.revert = {...this.floorplan};
            this.floorplan = new Floorplan(value);
            this.save();
        }
    },
    computed: {
        ...mapState(useAuthStore, ['user']),
        kovaStatus() {
            return FloorplanStatuses.label(this.floorplan.kova.CustomFields.TextCommModelStatus1);
        },
        lot() {
            let lotId = this.floorplan.kova?.Lot?.LotID;
            if(!lotId) return '-';
            return parseInt(lotId.split('-').pop());
        },
        statusLabel() {
            return Statuses.label(this.floorplan.status)
        },
        ...helpers
    },
    created() {
        this.show();
        this.tabs = tabs.filter(tab => {
            return this.user.level >= tab.acl
        });
    },
    beforeRouteEnter(to,from,next) {
        next(vm => {
            if(!vm.prev) vm.prev = (from.name && from) ?? false;
        })
    },
    components: {
        NotificationSubscription,
        LoadingView,
        ToggleButton,
        FormButton,
        TabPanes,
        InteractivePane,
        VideoPane,
        ImagePane,
        InfoPane,
        LogPane,
        PromotionsPane,
        InfoText,
        FeaturesPane
    },
}
</script>